import React, { useState } from 'react';
import { managerAPI, API_BASE_URL, API_ENDPOINTS } from '../config/api';
import APP_COLOURS from '../config/colors';
import Lottie from 'react-lottie';
import * as animationData from '../assets/lf30_editor_sdru9bf3.json';
import bg2 from '../assets/bg2.svg';
import tick from '../assets/tick_red.png';
import TraqLogo from './TraqLogo';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../theme';

const LoginPage = ({ onLoginSuccess, onSwitchToSignup }) => {
  const { scheme } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    passcode: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  
  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordEmailValid, setForgotPasswordEmailValid] = useState(false);
  const [forgotPasswordSubmitting, setForgotPasswordSubmitting] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  React.useEffect(() => {
    const handleResize = () => {
      setScreenHeight(window.innerHeight);
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    const prevOverflow = document.body.style.overflow;
    const prevHeight = document.body.style.height;
    const prevMinHeight = document.body.style.minHeight;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevHtmlHeight = document.documentElement.style.height;

    document.documentElement.style.backgroundColor = APP_COLOURS.BG;
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100%';
    document.body.style.backgroundColor = APP_COLOURS.BG;
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100%';
    document.body.style.minHeight = 'unset';

    return () => {
      document.documentElement.style.backgroundColor = '';
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.height = prevHtmlHeight;
      document.body.style.backgroundColor = prevBg;
      document.body.style.overflow = prevOverflow;
      document.body.style.height = prevHeight;
      document.body.style.minHeight = prevMinHeight;
    };
  }, [scheme]);

  const validateEmail = (text, setValid = setEmailValid) => {
    let reg = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (reg.test(text) === false) {
      setValid(false);
      return false;
    } else {
      setValid(true);
      return true;
    }
  };

  // Handle forgot password submission
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail.toLowerCase() })
      });

      if (!response.ok) {
        throw new Error('Failed to process request');
      }

      setForgotPasswordSuccess(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotPasswordError('An error occurred. Please try again.');
    } finally {
      setForgotPasswordSubmitting(false);
    }
  };

  // Close forgot password modal and reset state
  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordEmailValid(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      validateEmail(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Lowercase email for consistency
      const data = await managerAPI.login(formData.email.toLowerCase(), formData.passcode);
      
      localStorage.setItem('traq_access_token', data.accessToken);
      localStorage.setItem('traq_user_data', JSON.stringify(data));
      
      if (onLoginSuccess) {
        onLoginSuccess(data);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('We are unable to verify your profile, please check your email and password and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  let contain =
    screenWidth * 0.9 > screenHeight * 0.9
      ? screenHeight * 0.89
      : screenWidth * 0.9;

  let maxContain = contain > 520 ? 520 : contain;
  let ar = 636 / 545;

  // Forgot Password Modal JSX
  const forgotPasswordModalJSX = (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: APP_COLOURS.OVERLAY,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        backgroundColor: APP_COLOURS.BG2,
        borderRadius: 20,
        padding: 30,
        position: 'relative',
        boxShadow: `0 20px 60px ${APP_COLOURS.SHADOW}`,
      }}>
        {/* Close Button */}
        <button
          onClick={closeForgotPassword}
          style={{
            position: 'absolute',
            top: 15,
            right: 15,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={APP_COLOURS.TEXTCOLOR} strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {!forgotPasswordSuccess ? (
          <>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 25 }}>
              <div style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                backgroundColor: 'rgba(0, 104, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={APP_COLOURS.BLUE} strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </div>
              <h2 style={{
                color: APP_COLOURS.TEXTCOLOR,
                fontSize: 22,
                fontWeight: '600',
                margin: '0 0 8px',
                fontFamily: 'Poppins, sans-serif',
              }}>
                Forgot Password?
              </h2>
              <p style={{
                color: APP_COLOURS.TEXTCOLOR,
                fontSize: 14,
                margin: 0,
                opacity: 0.7,
                lineHeight: 1.5,
              }}>
                Enter your email address and we'll reset your password and send you a new one.
              </p>
            </div>

            {/* Error Message */}
            {forgotPasswordError && (
              <div style={{
                padding: 12,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderRadius: 8,
                marginBottom: 15,
              }}>
                <p style={{
                  color: APP_COLOURS.RED,
                  fontSize: 13,
                  margin: 0,
                  textAlign: 'center',
                }}>
                  {forgotPasswordError}
                </p>
              </div>
            )}

            {/* Email Form */}
            <form onSubmit={handleForgotPassword}>
              <div style={{
                width: '100%',
                padding: 12,
                backgroundColor: APP_COLOURS.BG,
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                boxSizing: 'border-box',
              }}>
                {forgotPasswordEmailValid && (
                  <img
                    src={tick}
                    alt="valid"
                    style={{
                      width: 18,
                      height: 18,
                      objectFit: 'contain',
                      marginRight: 8,
                      flexShrink: 0,
                    }}
                  />
                )}
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={forgotPasswordEmail}
                  onChange={(e) => {
                    setForgotPasswordEmail(e.target.value);
                    validateEmail(e.target.value, setForgotPasswordEmailValid);
                    if (forgotPasswordError) setForgotPasswordError('');
                  }}
                  autoFocus
                  required
                  disabled={forgotPasswordSubmitting}
                  style={{
                    width: '100%',
                    fontSize: 15,
                    color: APP_COLOURS.OFFWHITE,
                    fontWeight: '200',
                    fontFamily: 'Avenir, sans-serif',
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>

              {/* Submit Button */}
              <div style={{ marginTop: 20 }}>
                {!forgotPasswordSubmitting ? (
                  <button
                    type="submit"
                    disabled={!forgotPasswordEmailValid || forgotPasswordSubmitting}
                    style={{
                      backgroundColor: forgotPasswordEmailValid ? APP_COLOURS.BLUE : 'rgba(99, 102, 241, 0.3)',
                      width: '100%',
                      padding: 14,
                      borderRadius: 12,
                      border: 'none',
                      cursor: forgotPasswordEmailValid ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <span style={{
                      color: APP_COLOURS.WHITE,
                      fontSize: 15,
                      fontWeight: '600',
                    }}>
                      Send Password
                    </span>
                  </button>
                ) : (
                  <div style={{
                    backgroundColor: APP_COLOURS.BLUE,
                    padding: 10,
                    borderRadius: 12,
                    width: 44,
                    height: 44,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  </div>
                )}
              </div>
            </form>

            {/* Back to Login */}
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button
                type="button"
                onClick={closeForgotPassword}
                style={{
                  color: APP_COLOURS.TEXTCOLOR,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  opacity: 0.7,
                }}
              >
                ← Back to Login
              </button>
            </div>
          </>
        ) : (
          /* Success State */
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 70,
              height: 70,
              borderRadius: 35,
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 style={{
              color: APP_COLOURS.TEXTCOLOR,
              fontSize: 22,
              fontWeight: '600',
              margin: '0 0 12px',
              fontFamily: 'Poppins, sans-serif',
            }}>
              Password Reset!
            </h2>
            <p style={{
              color: APP_COLOURS.TEXTCOLOR,
              fontSize: 14,
              margin: '0 0 25px',
              opacity: 0.7,
              lineHeight: 1.6,
            }}>
              If an account with <strong style={{ color: APP_COLOURS.OFFWHITE }}>{forgotPasswordEmail}</strong> exists, a new password has been sent to your email. Please check your inbox.
            </p>
            <button
              onClick={closeForgotPassword}
              style={{
                backgroundColor: APP_COLOURS.BLUE,
                width: '100%',
                padding: 14,
                borderRadius: 12,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span style={{
                color: APP_COLOURS.WHITE,
                fontSize: 15,
                fontWeight: '600',
              }}>
                Back to Login
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const handlePasswordPaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const current = formData.passcode;
    const newValue = current.substring(0, start) + pasted + current.substring(end);
    e.preventDefault();
    setFormData(prev => ({ ...prev, passcode: newValue }));
    if (error) setError('');
  };

  // Mobile Layout
  if (screenWidth < 700) {
    return (
      <>
      <style>{`
        input, textarea, select {
          -webkit-tap-highlight-color: transparent !important;
          outline: none !important;
          outline-offset: 0 !important;
          box-shadow: none !important;
          -webkit-appearance: none !important;
        }
        input:focus, input:active,
        textarea:focus, textarea:active {
          outline: none !important;
          box-shadow: none !important;
        }
        *:focus-visible {
          outline: none !important;
        }
      `}</style>
      <div style={{
        background: `radial-gradient(circle at top right, rgba(0, 104, 255, 0.15) 0%, transparent 55%), radial-gradient(circle at bottom left, rgba(0, 104, 255, 0.07) 0%, transparent 45%), ${APP_COLOURS.BG}`,
        height: '100dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 20px',
        boxSizing: 'border-box',
        position: 'relative',
      }}>
        {/* Forgot Password Modal */}
        {showForgotPassword && forgotPasswordModalJSX}

        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 60 }}>
          <ThemeToggle compact />
        </div>
        
        {/* Logo */}
        <div style={{ marginBottom: 16, zIndex: 10 }}>
          <TraqLogo height={Math.min(52, screenWidth * 0.14)} />
        </div>

        {/* Tagline */}
        <div style={{
          marginBottom: 24,
          textAlign: 'center',
          zIndex: 10,
        }}>
          <p style={{
            fontSize: Math.max(13, screenWidth * 0.038),
            color: APP_COLOURS.TEXTCOLOR,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '300',
            margin: '0 0 4px',
            opacity: 0.75,
            lineHeight: 1.5,
          }}>
            Professional Staff & Retail management tools
          </p>
          <p style={{
            fontSize: Math.max(15, screenWidth * 0.044),
            color: APP_COLOURS.TEXTCOLOR,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '700',
            margin: 0,
            lineHeight: 1.3,
          }}>
            Manager Portal
          </p>
        </div>

        {/* Login Card */}
        <div style={{
          width: '100%',
          maxWidth: 400,
          padding: screenWidth < 380 ? 20 : 28,
          borderRadius: 20,
          backgroundColor: APP_COLOURS.BG2,
          zIndex: 10,
          boxSizing: 'border-box',
          border: `1px solid ${APP_COLOURS.CARD_BORDER}`,
          boxShadow: `0 8px 32px ${APP_COLOURS.SHADOW}`,
        }}>
         

          {/* Error Message */}
          {error && (
            <div style={{
              width: '100%',
              marginTop: 10,
              padding: 10,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderRadius: 8,
            }}>
              <p style={{
                color: APP_COLOURS.RED,
                fontSize: 12,
                margin: 0,
              }}>
                {error}
              </p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {/* Email Input */}
            <div style={{
              width: '100%',
              padding: 12,
              marginTop: 15,
              backgroundColor: APP_COLOURS.BG4,
              borderRadius: 12,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              boxSizing: 'border-box',
              border: `1px solid ${APP_COLOURS.CARD_BORDER}`,
              WebkitTapHighlightColor: 'transparent',
            }}>
              {emailValid && (
                <img
                  src={tick}
                  alt="valid"
                  style={{
                    width: 18,
                    height: 18,
                    objectFit: 'contain',
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
              )}
              <input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="email"
                autoFocus
                required
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  fontSize: 16,
                  color: APP_COLOURS.OFFWHITE,
                  fontWeight: '200',
                  fontFamily: 'Avenir, sans-serif',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  WebkitTapHighlightColor: 'transparent',
                  WebkitAppearance: 'none',
                }}
              />
            </div>

            {/* Password Input */}
            <div style={{
              width: '100%',
              padding: 12,
              marginTop: 12,
              backgroundColor: APP_COLOURS.BG4,
              borderRadius: 12,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              boxSizing: 'border-box',
              border: `1px solid ${APP_COLOURS.CARD_BORDER}`,
              WebkitTapHighlightColor: 'transparent',
            }}>
              {formData.passcode.length > 0 && (
                <img
                  src={tick}
                  alt="valid"
                  style={{
                    width: 18,
                    height: 18,
                    objectFit: 'contain',
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
              )}
              <input
                type="password"
                name="passcode"
                placeholder="Password"
                value={formData.passcode}
                onChange={handleInputChange}
                onPaste={handlePasswordPaste}
                autoComplete="current-password"
                required
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  fontSize: 16,
                  color: APP_COLOURS.OFFWHITE,
                  fontWeight: '200',
                  fontFamily: 'Avenir, sans-serif',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  WebkitTapHighlightColor: 'transparent',
                  WebkitAppearance: 'none',
                }}
              />
            </div>

            {/* Forgot Password Link */}
            <div style={{
              width: '100%',
              marginTop: 12,
              textAlign: 'right',
            }}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{
                  color: APP_COLOURS.BLUE,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: '500',
                  padding: 0,
                }}
              >
                Forgot Password?
              </button>
            </div>

            {/* Submit Button */}
            <div style={{
              width: '100%',
              marginTop: 20,
            }}>
              {!isSubmitting ? (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: APP_COLOURS.BLUE,
                    width: '100%',
                    padding: 14,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 12,
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <span style={{ 
                    color: APP_COLOURS.WHITE,
                    fontSize: 15,
                    fontWeight: '600',
                  }}>
                    Login
                  </span>
                </button>
              ) : (
                <div style={{
                  backgroundColor: APP_COLOURS.BLUE,
                  padding: 8,
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 12,
                  width: 40,
                  height: 40,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  display: 'flex',
                }}>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                </div>
              )}
            </div>

            {/* Sign Up Link */}
            <div style={{
              width: '100%',
              marginTop: 20,
              textAlign: 'center',
            }}>
              <p style={{
                color: APP_COLOURS.TEXTCOLOR,
                fontSize: 13,
                opacity: 0.7,
                margin: 0,
              }}>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={onSwitchToSignup}
                  style={{
                    color: APP_COLOURS.BLUE,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: 13,
                    fontWeight: '600',
                    padding: 0,
                  }}
                >
                  Sign up here
                </button>
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 24,
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingRight: 4,
          paddingLeft: 4,
          zIndex: 10,
        }}>
          <p style={{
            fontSize: 14,
            color: APP_COLOURS.BLUE,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '800',
            margin: 0,
          }}>
            tetrice
          </p>
          <p style={{
            fontSize: 12,
            color: APP_COLOURS.TEXTCOLOR,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '300',
            margin: 0,
            opacity: 0.7,
          }}>
            info@traq.app
          </p>
        </div>
      </div>
      </>
    );
  }

  const isMedium = screenWidth >= 700 && screenWidth < 1024;

  return (
    <div style={{
      width: screenWidth,
      height: screenHeight,
      backgroundColor: APP_COLOURS.BG,
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Forgot Password Modal */}
      {showForgotPassword && forgotPasswordModalJSX}

      <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 60 }}>
        <ThemeToggle />
      </div>
      
      {/* Background Pattern */}
      <div style={{
        width: screenWidth,
        height: screenHeight,
        right: 0,
        bottom: 0,
        position: 'absolute',
      }}>
        <img
          src={bg2}
          alt="background"
          style={{
            width: maxContain * ar,
            height: maxContain,
            objectFit: 'contain',
            position: 'absolute',
            left: -10,
            bottom: -10,
          }}
        />
      </div>

      {/* Lottie Animation */}
      <div style={{
        position: 'absolute',
        bottom: '10%',
        left: '1%',
      }}>
        <Lottie
          options={defaultOptions}
          height={contain > 600 ? 600 : contain}
          width={contain > 600 ? 600 : contain}
        />
      </div>

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        width: screenWidth,
        height: screenHeight,
      }}>
        {/* Left Side - Logo */}
        <div style={{ flex: 1 }}>
          <div style={{
            position: 'absolute',
            top: '5%',
            left: '2%',
          }}>
            <TraqLogo height={48} />
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          flexDirection: 'column',
        }}>
          <div style={{
            width: isMedium ? Math.min(320, screenWidth * 0.42) : 307,
            height: 'auto',
            padding: isMedium ? 24 : 20,
            borderRadius: 25,
            backgroundColor: APP_COLOURS.BG2,
            position: 'relative',
            zIndex: 10,
            boxSizing: 'border-box',
            border: `1px solid ${APP_COLOURS.CARD_BORDER}`,
            boxShadow: `0 8px 32px ${APP_COLOURS.SHADOW}`,
          }}>
            {/* Logo at top of form */}
            <TraqLogo height={44} style={{ margin: '5px auto' }} />

            {/* Error Message */}
            {error && (
              <div style={{
                width: '100%',
                marginTop: 15,
                padding: 10,
              }}>
                <p style={{
                  color: APP_COLOURS.RED,
                  fontSize: 12,
                }}>
                  {error}
                </p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              {/* Email Input */}
              <div style={{
                width: '100%',
                padding: 10,
                marginTop: 15,
                backgroundColor: APP_COLOURS.BG4,
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                border: `1px solid ${APP_COLOURS.CARD_BORDER}`,
              }}>
                {emailValid && (
                  <img
                    src={tick}
                    alt="valid"
                    style={{
                      width: 20,
                      height: 20,
                      objectFit: 'contain',
                      marginRight: 10,
                    }}
                  />
                )}
                <input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  autoFocus
                  required
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    fontSize: 16,
                    textAlign: 'left',
                    color: APP_COLOURS.OFFWHITE,
                    fontWeight: '200',
                    fontFamily: 'Avenir, sans-serif',
                    letterSpacing: 0.26,
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>

              {/* Password Input */}
              <div style={{
                width: '100%',
                padding: 10,
                marginTop: 15,
                backgroundColor: APP_COLOURS.BG4,
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                border: `1px solid ${APP_COLOURS.CARD_BORDER}`,
              }}>
                {formData.passcode.length > 0 && (
                  <img
                    src={tick}
                    alt="valid"
                    style={{
                      width: 20,
                      height: 20,
                      objectFit: 'contain',
                      marginRight: 10,
                    }}
                  />
                )}
                <input
                  type="password"
                  name="passcode"
                  placeholder="Password"
                  value={formData.passcode}
                  onChange={handleInputChange}
                  onPaste={handlePasswordPaste}
                  autoComplete="current-password"
                  required
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    fontSize: 16,
                    textAlign: 'left',
                    color: APP_COLOURS.OFFWHITE,
                    fontWeight: '200',
                    fontFamily: 'Avenir, sans-serif',
                    letterSpacing: 0.26,
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                  }}
                />
              </div>

              {/* Forgot Password Link */}
              <div style={{
                width: '100%',
                marginTop: 10,
                textAlign: 'right',
              }}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  style={{
                    color: APP_COLOURS.BLUE,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: '500',
                    padding: 0,
                  }}
                >
                  Forgot Password?
                </button>
              </div>

              {/* Submit Button */}
              <div style={{
                width: '100%',
                marginTop: 15,
              }}>
                {!isSubmitting ? (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      backgroundColor: APP_COLOURS.BLUE,
                      width: '100%',
                      padding: 10,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: 15,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    <span style={{ color: APP_COLOURS.WHITE }}>
                      Login
                    </span>
                  </button>
                ) : (
                  <div style={{
                    backgroundColor: APP_COLOURS.BLUE,
                    padding: 6,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 15,
                    width: 30,
                    height: 30,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    display: 'flex',
                  }}>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  </div>
                )}
              </div>

              {/* Sign Up Link */}
              <div style={{
                width: '100%',
                marginTop: 15,
                textAlign: 'center',
              }}>
                <p style={{
                  color: APP_COLOURS.TEXTCOLOR,
                  fontSize: 12,
                  opacity: 0.7,
                }}>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={onSwitchToSignup}
                    style={{
                      color: APP_COLOURS.BLUE,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: 12,
                      fontWeight: '600',
                    }}
                  >
                    Sign up here
                  </button>
                </p>
              </div>
            </form>
          </div>

          {/* Bottom Text */}
          <div style={{ paddingLeft: isMedium ? 16 : 30 }}>
            <p style={{
              fontSize: isMedium ? 20 : 26,
              color: APP_COLOURS.TEXTCOLOR,
              fontFamily: 'Poppins, sans-serif',
              fontWeight: '400',
              margin: 0,
            }}>
              Professional Staff &
            </p>
            <p style={{
              fontSize: isMedium ? 20 : 26,
              color: APP_COLOURS.TEXTCOLOR,
              fontFamily: 'Poppins, sans-serif',
              fontWeight: '400',
              margin: 0,
            }}>
              Retail management tools
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        width: screenWidth,
        position: 'absolute',
        bottom: 30,
        left: 0,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingRight: 30,
        paddingLeft: 30,
      }}>
        <p style={{
          width: 110,
          fontSize: 16,
          color: APP_COLOURS.TEXTCOLOR,
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '800',
        }}>
          tetrice
        </p>
        <p style={{
          width: 110,
          fontSize: 13,
          color: APP_COLOURS.TEXTCOLOR,
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '300',
        }}>
          info@traq.app
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
